import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import EventName from "@/components/EventName";

// Compute the English counterpart URL for the current Arabic path.
// Arabic root "/ar" -> "/". Arabic "/ar/foo/bar" -> "/foo/bar".
function toEnglishPath(pathname) {
    if (pathname === "/ar" || pathname === "/ar/") return "/";
    if (pathname.startsWith("/ar/")) return pathname.slice(3); // remove "/ar" prefix, keeps leading "/"
    return "/";
}

// Arabic navigation labels. "The Gathering" is a protected English
// institutional asset (PRD §34.2) and remains in English within the
// Arabic navigation, rendered LTR-isolated within the RTL flow.
export const PRIMARY_NAV_AR = [
    { to: "/ar", label: "الرئيسية", isProtectedEn: false },
    { to: "/ar/about", label: "كروزنيشن", isProtectedEn: false },
    { to: "/ar/gathering", label: "THE GATHERING", isProtectedEn: true },
    { to: "/ar/culture", label: "ثقافتنا", isProtectedEn: false },
    { to: "/ar/app", label: (<>تطبيق <span dir="ltr">CREWZ</span></>), isProtectedEn: false },
    { to: "/ar/sponsors", label: "الرعاة", isProtectedEn: false },
    { to: "/ar/store", label: "المتجر", isProtectedEn: false },
    { to: "/ar/contact", label: "تواصل", isProtectedEn: false },
];

// Render nav label. Protected English tokens (site-wide brand asset
// "THE GATHERING") are rendered via the shared EventName wordmark
// component — uppercase, LTR-isolated, THE white, GATHERING gold —
// so they retain their institutional identity inside the Arabic
// RTL flow and stay in lock-step with the English site.
function renderNavLabel(item) {
    if (item.isProtectedEn) {
        return <EventName />;
    }
    return item.label;
}

export default function NavbarAr() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const englishHref = toEnglishPath(location.pathname);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            className="sticky top-0 z-50 bg-[#111111]/90 backdrop-blur-xl border-b border-white/5"
            data-testid="site-navbar-ar"
            dir="rtl"
        >
            <div className="relative px-6 md:px-10 lg:px-16 h-24 flex items-center justify-between">
                <nav
                    className="hidden lg:flex flex-1 items-center justify-between gap-1 font-arabic text-[16.2px] font-bold order-2 lg:ml-8 xl:ml-12"
                    aria-label="Primary Arabic navigation"
                >
                    {PRIMARY_NAV_AR.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/ar"}
                            className={({ isActive }) =>
                                `px-3 py-2 text-center transition-colors duration-300 ${
                                    isActive
                                        ? "text-[var(--crewz-gold)]"
                                        : "text-zinc-200 hover:text-white"
                                }`
                            }
                            data-testid={`nav-link-ar-${l.to.replace(/^\/ar\/?/, "") || "home"}`}
                        >
                            {renderNavLabel(l)}
                        </NavLink>
                    ))}
                    <Link
                        to={englishHref}
                        dir="ltr"
                        className="font-body uppercase tracking-[0.2em] px-3 py-2 text-[13.6px] font-bold text-zinc-200 hover:text-white transition-colors duration-300 lg:ml-16"
                        data-testid="nav-lang-switcher-ar"
                        aria-label="Switch to English"
                    >
                        English
                    </Link>
                </nav>

                {/* Logo — on lg+ the logo is absolutely positioned at the
                    left edge of the header so it does NOT participate in
                    the flex sizing of `<nav>`. This lets the Arabic nav
                    span the same physical width as the English navbar and
                    have its items distribute with `justify-between` in a
                    pixel-mirrored composition. On <lg (mobile/tablet) the
                    logo remains an in-flow flex child (order-1) so the
                    header still balances against the hamburger. */}
                <Link
                    to="/ar"
                    className="flex items-center gap-3 order-1 lg:order-4 lg:absolute lg:top-1/2 lg:left-16 lg:-translate-y-1/2 lg:z-10"
                    data-testid="nav-logo-ar"
                >
                    <img
                        src={LOGO_URL}
                        alt="CrewZNatioN"
                        className="h-14 md:h-16 w-auto select-none"
                        draggable={false}
                    />
                </Link>

                <button
                    className="lg:hidden text-white p-2 order-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="nav-mobile-toggle-ar"
                    aria-label="فتح القائمة"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    className="lg:hidden border-t border-white/5 bg-[#111111]/95 backdrop-blur-xl"
                    data-testid="nav-mobile-menu-ar"
                >
                    <div className="px-6 py-6 flex flex-col gap-1 font-arabic">
                        {PRIMARY_NAV_AR.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/ar"}
                                className={({ isActive }) =>
                                    `px-2 py-3 text-sm border-b border-white/5 font-bold ${
                                        isActive
                                            ? "text-[var(--crewz-gold)]"
                                            : "text-zinc-300"
                                    }`
                                }
                            >
                                {renderNavLabel(l)}
                            </NavLink>
                        ))}
                        <Link
                            to={englishHref}
                            dir="ltr"
                            className="font-body uppercase tracking-[0.2em] px-2 py-3 text-xs font-bold text-zinc-300 border-b border-white/5"
                            data-testid="nav-mobile-lang-switcher-ar"
                            aria-label="Switch to English"
                        >
                            English
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
